import { useState } from 'react';
import { rsme, type RepairTicket } from '../lib/rsme';
import { useQuery } from '../ui/useQuery';
import { PageHead, Card, Pill, Input, Loading, ErrorState, EmptyState, StatusDot } from '../ui/primitives';

const STATUSES: RepairTicket['status'][] = ['open', 'in_progress', 'resolved', 'closed'];

export default function Repairs() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<RepairTicket['status'] | ''>('open');
  const q = useQuery(() => rsme.repairTickets.list({ search, status: status || undefined }), [search, status]);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHead title="Repairs" sub="Workshop queue · creating a ticket auto-pings WhatsApp" />
      <div className="px-5 md:px-8 flex flex-wrap gap-2 items-center">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket, asset, description" className="md:max-w-sm" />
        <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="h-10 px-3 rounded-lg bg-elevated border border-line text-sm">
          <option value="">all</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="px-5 md:px-8 mt-4 space-y-2">
        {q.loading && <Loading rows={5} />}
        {q.error && <ErrorState error={q.error} retry={q.refetch} />}
        {!q.loading && !q.error && !q.data?.data?.length && <EmptyState title="No tickets" hint="Workshop is clear." />}
        {q.data?.data?.map((t) => (
          <Card key={t.id} className="!p-4">
            <div className="flex items-start gap-3">
              <StatusDot tone={t.priority === 'urgent' || t.priority === 'high' ? 'danger' : t.priority === 'medium' ? 'warn' : 'muted'} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold tabular-nums">{t.ticketNo}</span>
                  <span className="text-sm font-medium truncate">{t.assetName}</span>
                  <Pill tone={t.status === 'open' ? 'danger' : t.status === 'in_progress' ? 'warn' : 'good'}>{t.status}</Pill>
                  {t.priority && <Pill tone={t.priority === 'urgent' ? 'danger' : t.priority === 'high' ? 'warn' : 'muted'}>{t.priority}</Pill>}
                </div>
                <div className="text-sm text-muted mt-1 line-clamp-2">{t.description}</div>
                <div className="text-xs text-muted mt-1">{t.serviceType ?? '—'} · {t.assignedMechanic ?? 'unassigned'} · {t.estimatedRepairDate ?? 'no eta'}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
